import React, { useEffect, useState } from 'react';
import { adminUpdateAppUser } from '../../../api/appUsersApi';
import './styles/UserSecurity.css';

const UserSecurity = ({ user, setUser }) => {
  const userId = user?.id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  
  const [securitySettings, setSecuritySettings] = useState({
    isBlocked: user?.isBlocked === true || user?.status === 'blocked',
    twoFactorEnabled: user?.twoFactorEnabled === true,
    emailVerified: user?.emailVerified !== false,
    requirePasswordChange: user?.requirePasswordChange === true
  });

  useEffect(() => {
    setSecuritySettings({
      isBlocked: user?.isBlocked === true || user?.status === 'blocked',
      twoFactorEnabled: user?.twoFactorEnabled === true,
      emailVerified: user?.emailVerified !== false,
      requirePasswordChange: user?.requirePasswordChange === true
    });
  }, [user?.id, user?.isBlocked, user?.status, user?.twoFactorEnabled, user?.emailVerified, user?.requirePasswordChange]);

  const handleToggle = async (setting) => {
    setActionType(setting);
    setConfirmDialog(true);
  };

  const closeDialog = () => {
    if (loading) return;
    setConfirmDialog(false);
    setActionType(null);
  };

  const handleConfirmAction = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const newValue = !securitySettings[actionType];
      const updated = await adminUpdateAppUser(userId, { [actionType]: newValue });
      setSecuritySettings((prev) => ({
        ...prev,
        [actionType]: newValue
      }));
      if (typeof setUser === 'function' && updated) {
        setUser((prev) => ({ ...prev, ...updated }));
      }

      setSuccess(true);
    } catch (err) {
      setError('Error updating security settings');
      console.error(err);
    } finally {
      setLoading(false);
      setConfirmDialog(false);
    }
  };

  const handleResetPassword = async () => {
    // Implement password reset logic here
    // This could involve sending a reset email or generating a temporary password
    setSuccess(false);
    setError('Password reset is not wired yet on this screen.');
  };

  return (
    <div className="admin-card user-section-card userSecurityCard" aria-busy={loading ? 'true' : 'false'}>
      {success ? (
        <div className="usAlert usAlertSuccess" role="status" aria-live="polite">
          Security settings updated successfully.
        </div>
      ) : null}

      {error ? (
        <div className="usAlert usAlertError" role="alert" aria-live="polite">
          {error}
        </div>
      ) : null}

      <div className="usHeader">
        <div>
          <h3 className="usTitle">Security Settings</h3>
          <p className="usSub">Manage account access and security flags.</p>
        </div>
      </div>

      <div className="usRows">
        <div className="usRow">
          <div>
            <p className="usRowTitle">Account status</p>
            <p className="usRowSub">{securitySettings.isBlocked ? 'Blocked' : 'Active'}</p>
          </div>
          <label className="usSwitch" htmlFor="usIsBlocked" aria-label="Account status">
            <input
              id="usIsBlocked"
              type="checkbox"
              checked={!securitySettings.isBlocked}
              onChange={() => handleToggle('isBlocked')}
              disabled={loading}
            />
            <span className="usSwitchTrack">
              <span className="usSwitchThumb" />
            </span>
          </label>
        </div>

        <div className="usRow">
          <div>
            <p className="usRowTitle">Two-factor authentication</p>
            <p className="usRowSub">Require 2FA for additional security.</p>
          </div>
          <label className="usSwitch" htmlFor="usTwoFactor" aria-label="Two-factor authentication">
            <input
              id="usTwoFactor"
              type="checkbox"
              checked={securitySettings.twoFactorEnabled}
              onChange={() => handleToggle('twoFactorEnabled')}
              disabled={loading}
            />
            <span className="usSwitchTrack">
              <span className="usSwitchThumb" />
            </span>
          </label>
        </div>

        <div className="usRow">
          <div>
            <p className="usRowTitle">Email verification</p>
            <p className="usRowSub">{securitySettings.emailVerified ? 'Verified' : 'Not verified'}</p>
          </div>
          <label className="usSwitch" htmlFor="usEmailVerified" aria-label="Email verification">
            <input
              id="usEmailVerified"
              type="checkbox"
              checked={securitySettings.emailVerified}
              onChange={() => handleToggle('emailVerified')}
              disabled={loading}
            />
            <span className="usSwitchTrack">
              <span className="usSwitchThumb" />
            </span>
          </label>
        </div>

        <div className="usRow">
          <div>
            <p className="usRowTitle">Force password reset</p>
            <p className="usRowSub">User will be required to change password on next login.</p>
          </div>
          <label className="usSwitch" htmlFor="usRequirePwChange" aria-label="Force password reset">
            <input
              id="usRequirePwChange"
              type="checkbox"
              checked={securitySettings.requirePasswordChange}
              onChange={() => handleToggle('requirePasswordChange')}
              disabled={loading}
            />
            <span className="usSwitchTrack">
              <span className="usSwitchThumb" />
            </span>
          </label>
        </div>

        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="usBtn" onClick={handleResetPassword} disabled={loading}>
            Send password reset email
          </button>
        </div>
      </div>

      {confirmDialog ? (
        <div
          className="usModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm action"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDialog();
          }}
        >
          <div className="usModal">
            <h4 className="usModalTitle">Confirm change</h4>
            <p className="usModalBody">Are you sure you want to change this security setting?</p>
            <div className="usModalActions">
              <button type="button" className="usBtn" onClick={closeDialog} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="usBtn usBtnPrimary" onClick={handleConfirmAction} disabled={loading}>
                {loading ? (
                  <>
                    <span className="usSpinner" aria-hidden="true" />
                    Confirming…
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserSecurity; 